import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { environments } from '@/utils/helpers';
import { validateToken } from '@/utils/auth';
import {
  ACCESS_TOKEN_KEY,
  AUTH_TOKENS_CHANGED_EVENT,
  readTokens,
  refreshSession
} from '@/data/authSession';
import { InboxNotification } from '@/types/Notification.interface';

/** Başarılı bir el sıkışma olmadan istenebilecek en fazla ortak yenileme. */
const MAX_AUTH_REFRESHES = 3;

interface UseNotificationSocketOptions {
  onUnreadCount?: (count: number) => void;
  onNewNotification?: (notification: InboxNotification) => void;
  onRefresh?: () => void;
}

export function useNotificationSocket({
  onUnreadCount,
  onNewNotification,
  onRefresh
}: UseNotificationSocketOptions) {
  const socketRef = useRef<Socket | null>(null);

  // Use refs for callbacks to avoid reconnecting when callbacks change
  const onUnreadCountRef = useRef(onUnreadCount);
  const onNewNotificationRef = useRef(onNewNotification);
  const onRefreshRef = useRef(onRefresh);

  useEffect(() => {
    onUnreadCountRef.current = onUnreadCount;
    onNewNotificationRef.current = onNewNotification;
    onRefreshRef.current = onRefresh;
  }, [onUnreadCount, onNewNotification, onRefresh]);

  useEffect(() => {
    if (!readTokens().accessToken) return;

    const baseUrl = environments.backendBaseUrl;
    if (!baseUrl) return;

    let handshakeToken: string | null = null;
    let awaitingNewToken = false;
    let disposed = false;

    const socket = io(`${baseUrl}/notifications`, {
      // Her bağlantıda (yeniden bağlanmalar dahil) saklı güncel anahtar okunur.
      auth: (callback) => {
        handshakeToken = readTokens().accessToken;
        callback({ token: handshakeToken });
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 10
    });

    socketRef.current = socket;

    // Sunucu bağlantıyı kendisi kestiğinde socket.io yeniden bağlanmaz; yeni
    // bir anahtar saklanınca bir kez yeniden bağlanılır.
    const reconnectWithNewToken = () => {
      if (disposed || !awaitingNewToken) return;
      const { accessToken } = readTokens();
      if (!accessToken || accessToken === handshakeToken) return;
      awaitingNewToken = false;
      socket.connect();
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === ACCESS_TOKEN_KEY) reconnectWithNewToken();
    };
    window.addEventListener(AUTH_TOKENS_CHANGED_EVENT, reconnectWithNewToken);
    window.addEventListener('storage', onStorage);

    // Yeni anahtar beklenirken istenen ortak yenileme sayısı; başarılı bir
    // el sıkışmada sıfırlanır. Sunucu taze anahtarları da reddederse döngü
    // burada durur.
    let authRefreshes = 0;

    const waitForNewToken = (refreshRefusedToken: boolean) => {
      awaitingNewToken = true;
      reconnectWithNewToken();
      if (!awaitingNewToken || !refreshRefusedToken || !handshakeToken) return;
      if (authRefreshes >= MAX_AUTH_REFRESHES) return;
      authRefreshes++;
      refreshSession(handshakeToken).then(reconnectWithNewToken, () => {});
    };

    socket.on('connect', () => {
      authRefreshes = 0;
    });

    // Yeni backend anahtarı el sıkışmada reddeder; socket.io bu durumda
    // kendiliğinden yeniden denemez. TOKEN_EXPIRED ortak yenilemeyi ister,
    // UNAUTHORIZED yalnızca farklı bir anahtarın saklanmasını bekler.
    socket.on('connect_error', (err) => {
      const code = (err as Error & { data?: { code?: string } }).data?.code;
      if (code === 'TOKEN_EXPIRED' || code === 'UNAUTHORIZED') {
        waitForNewToken(code === 'TOKEN_EXPIRED');
        return;
      }
      console.warn('[NotificationSocket] Connection error:', err.message);
    });

    // Eski backend süresi dolmuş anahtarı bağlandıktan sonra atar. Yenileme
    // yalnızca yerel saate göre süresi dolmuş anahtar için istenir; geçerli
    // anahtar atıldıysa döngüye girmemek için beklenir.
    socket.on('disconnect', (reason) => {
      if (reason !== 'io server disconnect') return;
      waitForNewToken(
        Boolean(handshakeToken) && !validateToken(handshakeToken as string)
      );
    });

    socket.on('notification:unreadCount', (data: { count: number }) => {
      onUnreadCountRef.current?.(data.count);
    });

    socket.on('notification:new', (data: InboxNotification) => {
      onNewNotificationRef.current?.(data);
    });

    socket.on('notification:refresh', () => {
      onRefreshRef.current?.();
    });

    return () => {
      disposed = true;
      window.removeEventListener(
        AUTH_TOKENS_CHANGED_EVENT,
        reconnectWithNewToken
      );
      window.removeEventListener('storage', onStorage);
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  const disconnect = useCallback(() => {
    socketRef.current?.disconnect();
    socketRef.current = null;
  }, []);

  return { disconnect };
}
